import React, { ReactElement, useState } from "react";
import TimeAgo from "react-timeago";
import { BsEmojiSunglasses } from "react-icons/bs";
import { Popover, OverlayTrigger, Overlay } from "react-bootstrap";
import {
    TicketMessage, TicketMessageReaction, TicketMessageType, useAddReactionMutation, User, useRemoveReactionMutation,
} from "../../../__generated__/api";
import ArticlePreview from "../../agent/answer-finder/ArticlePreview";
import Attachment from "../../attachment/Attachment";
import Avatar from "../../avatar/Avatar";
import { HtmlRenderer } from "../../html-render/HtmlRenderer";
import CardActions from "../card-actions";
import Styles from "../Chat.module.scss";
import EmojiShort from "../../emoji-picker/emoji-short.json";
import EmojiPicker from "../../emoji-picker/EmojiPicker";
import UserOverlay from "../../user-overlay/UserOverlay";

interface BodyProps {
    chatId: string;
    message: TicketMessage;
    className: string;
    attachmentClassName: string;
    isHover: boolean
}
function Body({
    chatId, message, className, attachmentClassName, isHover,
}: BodyProps): ReactElement {
    const [addReaction] = useAddReactionMutation();
    const [removeReaction] = useRemoveReactionMutation();

    const [target, setTarget] = useState<HTMLElement>();

    const handleClick = async (reactions: TicketMessageReaction[]) => {
        const isCurrent = reactions.some(row => row.isCreatedByCurrentUser);
        const reaction = {
            chatId,
            messageId: message.id,
            reaction: reactions.firstElement.reaction,
        };
        if (isCurrent) {
            await removeReaction({
                variables: {
                    reaction,
                },
            });
        } else {
            await addReaction({
                variables: {
                    reaction,
                },
            });
        }
    };

    return (
        <div className="overflow-auto">
            {message.body.html && (
                <HtmlRenderer className={className} html={message.body.html} />
            )}

            {message.attachments?.isNotEmpty && (
                <div className={`d-flex flex-wrap mb-3 flex-grow-1 ${attachmentClassName}`}>
                    {message.attachments?.map(attach => (
                        <Attachment
                            className="px-2 py-2"
                            mimeType={attach.mimeType as string}
                            title={attach.title ?? attach.fileName as string}
                            url={attach.url as string}
                            key={attach.url}
                        />
                    ))}
                </div>
            )}

            {message.articles?.isNotEmpty && (
                <div className={`d-flex flex-wrap mb-3 flex-grow-1 ${attachmentClassName}`}>
                    {message.articles?.map(article => (
                        <ArticlePreview
                            className={`px-2 py-2 ${Styles["reference-answer"]}`}
                            article={article}
                        />
                    ))}
                </div>
            )}

            {message.reactions?.isNotEmpty && (
                <div className="mt-n2 d-flex">
                    {Object.values(message.reactions.groupBy(row => row.reaction)).map(group => {
                        const first = group.firstElement;
                        const isCreatedByCurrentUser = group.some(row => row.isCreatedByCurrentUser);
                        const emoji = EmojiShort[`:${first.reaction}:` as keyof typeof EmojiShort] ?? `:${first.reaction}:`;
                        let border = "border-gray-100";
                        if (isHover) {
                            border = "border-light";
                        }
                        if (isCreatedByCurrentUser) {
                            border = "border-gray-400";
                        }

                        return (
                            <button
                                className={`badge btn btn-none rounded-pill border bg-gray-100
                                            ${border}
                                            text-blue-light px-2 py-1 me-1 ${Styles.reaction}`}
                                type="button"
                                onClick={() => handleClick(group)}
                                title={isCreatedByCurrentUser ? "Added by you" : "Added by others"}
                                key={first.reaction}
                            >
                                {emoji}
                                <span className="px-1 ps-2">
                                    {group.length}
                                </span>
                            </button>
                        );
                    })}

                    {target && (
                        <EmojiPicker
                            target={target}
                            chatId={chatId}
                            messageId={message.id}
                            onHide={() => setTarget(undefined)}
                        />
                    )}

                    {/* <button
                        className={`badge btn btn-none rounded-pill
                                    py-1 px-2 me-2 ${Styles["reaction-add"]} text-light`}
                        type="button"
                        onClick={evt => {
                            setTarget(target ? undefined : evt.currentTarget);
                        }}
                    >
                        <BsEmojiSunglasses />
                    </button> */}

                </div>
            )}

        </div>
    );
}

export interface MessageCardProps {
    chatId: string;
    message: TicketMessage;
    previous: TicketMessage | undefined;
}

export default function MessageCard({ chatId, message, previous }: MessageCardProps): ReactElement {
    const isAgentMessage = message.type === TicketMessageType.AgentMessage || message.type === TicketMessageType.Triggered;
    const date = new Date(message.createdDate);
    const [isHover, setHover] = useState(false);
    const [isDisplayProfile, setIsDisplayProfile] = useState(false);

    if (message.creatorId === previous?.creatorId && message.type === previous?.type) {
        const classNames = ["position-relative d-flex align-items-top py-1 mt-1 "];
        if (isAgentMessage) {
            classNames.push("justify-content-end");
        }
        if (message.type === TicketMessageType.Triggered) {
            classNames.push("text-light-400");
        }

        if (isHover) {
            classNames.push("bg-gray-100 rounded-2 mx-n2 px-2");
        }
        return (
            <div
                className={classNames.join(" ")}
                onMouseOver={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onFocus={() => setHover(true)}
            >
                <div
                    className={`${Styles.avatar} flex-shrink-0 position-relative ${isAgentMessage ? "order-2 ms-4" : "ms-3"}`}
                />

                <Body
                    chatId={chatId}
                    className=""
                    message={message}
                    isHover={isHover}
                    attachmentClassName={isAgentMessage ? "justify-content-end" : "justify-content-start"}
                />

                {message.body.html
                    && (message.type === TicketMessageType.AgentMessage || message.type === TicketMessageType.UserMessage)
                    && (
                        <CardActions
                            isAgentMessage={isAgentMessage}
                            messageText={message.body.html}
                            plainText={message.body.plaintext as string}
                            isHover={isHover}
                            chatId={chatId}
                            messageId={message.id}
                        />
                    )}

            </div>
        );
    }

    return (
        <div
            className={`position-relative py-1 mt-1 d-flex align-items-start ${isAgentMessage ? "justify-content-end" : ""}
            ${isHover ? "bg-gray-100 rounded-2 mx-n2 px-2" : ""}`}
            onMouseOver={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocus={() => setHover(true)}
        >
            {message.creator?.id ? (
                <UserOverlay
                    size="lg"
                    user={message.creator as User}
                    onToggleShow={value => setIsDisplayProfile(value)}
                    shouldShow={isDisplayProfile}
                    placement={isAgentMessage ? "left" : "right"}
                >
                    <button
                        type="button"
                        className={`btn btn-none p-0 ${isAgentMessage ? "order-2 ms-3 mt-1 me-2" : "me-3 mt-1"}`}
                        onClick={() => setIsDisplayProfile(!isDisplayProfile)}
                    >
                        <Avatar
                            src={message.creator?.avatarUrl as string}
                            variant="sm"
                            isCircle={false}
                            className="p-0 m-0"
                        />
                    </button>

                </UserOverlay>
            ) : (
                <Avatar
                    src={message.creator?.avatarUrl as string}
                    variant="sm"
                    isCircle={false}
                    className={`${Styles.avatar} ${isAgentMessage ? "order-2 ms-3 mt-1 me-2" : "me-3 mt-1"}`}
                />
            )}

            <div className={`overflow-auto ${message.type === TicketMessageType.Triggered ? "text-light-400" : ""}`}>
                <div className={`d-flex flex-rows align-items-center ${isAgentMessage ? "justify-content-end" : ""}`}>
                    <strong className={isAgentMessage ? "order-2 ms-3" : "me-3"}>
                        {message.creator?.name ?? "Agent"}
                    </strong>
                    <small className="text-light">
                        <TimeAgo
                            date={date}
                            minPeriod={60}
                            title={`${date.toLocaleDateString()} ${date.toLocaleTimeString()}`}
                        />
                    </small>
                </div>

                <Body chatId={chatId} className="" message={message} attachmentClassName="" isHover={isHover} />
                {message.body.html
                    && (message.type === TicketMessageType.AgentMessage || message.type === TicketMessageType.UserMessage)
                    && (
                        <CardActions
                            isAgentMessage={isAgentMessage}
                            messageText={message.body.html}
                            plainText={message.body.plaintext as string}
                            isHover={isHover}
                            chatId={chatId}
                            messageId={message.id}
                        />
                    )}
            </div>

        </div>
    );
}
